import { Component, OnDestroy, ViewChild } from '@angular/core';
import { UploadManager } from '../../../managers/upload.manager';
import { asyncScheduler, filter, forkJoin, map, Observable, observeOn, switchMap, take } from 'rxjs';
import { Upload, UploadBatchValidationStatus } from '../../../models/ui/upload.model';
import { BaseComponent } from '../../base.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { JobType } from '../../../models/ui/job-request/job.type';
import { ProcessingManager } from '../../../managers/processing.manager';
import { DeNistJobRequest } from '../../../models/ui/job-request/de-nist-job-request.model';
import { NoParamJobRequest } from '../../../models/ui/job-request/no-param-job-request.model';
import { remove } from 'lodash-es';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { DeduplicateJobRequest } from '../../../models/ui/job-request/deduplicate-job-request.model';
import { HelpService } from '../../../services/help.service';
import { MatSort } from '@angular/material/sort';
import { InsightsUtility } from '../../../utilities/insights/insights.utility';
import { GeneralError } from '../../../insights/errors/general.error';
import { UploadWindowClosedEvent } from '../../../insights/events/upload-window-closed.event';
import { UploadBatchValidationJobRequest } from 'src/app/models/ui/job-request/upload-batch-validation-job-request.model';
import { UploadBatchType, UploadBatchTypeLookup } from '@models/upload-batch-type.enum';
import { UserConfigsService } from '@services/user-configs.service';
import { Role } from '@models/role.model';
import { UploadBatchesSdk } from "@sdks/upload-batches.sdk";
import { NotificationManager } from '@managers/notification.manager';
import { UploadBatchesClient } from "@clients/api.client";
import { tap } from "rxjs/operators";

@Component({
    selector: 'lex-upload-batches',
    templateUrl: './uploads.component.html',
    styleUrls: ['./uploads.component.scss'],
    standalone: false
})
export class UploadsComponent extends BaseComponent implements OnDestroy {
	@ViewChild('paginator', { static: false }) paginator: MatPaginator;
	@ViewChild(MatSort, { static: false }) matSort: MatSort;

	protected readonly UploadBatchType = UploadBatchType;
	public readonly UploadBatchTypeLookup = UploadBatchTypeLookup;
	public Role = Role;

	public dataSource$: Observable<MatTableDataSource<Upload>>;
	public totalEntries$: Observable<number>;
	public hasNextPage$: Observable<boolean>;
	public hasPreviousPage$: Observable<boolean>;

	public columnsToDisplay = [
		'selecto-checko',
		'title',
		'date',
		'utcOffset',
		'comments',
		'username',
		'custodian',
		'source',
		'typeId',
		'validationStatus',
		'manage-actions'
	];

	public validationStatus = UploadBatchValidationStatus;
	public selectedUploadIds: string[] = [];
	private skip: number = 0;
	private take: number = 25;
	private sortColumn: string = 'uploadBatchDate';
	private sortDirection: 'asc' | 'desc' = 'desc';
	private refreshInterval;

	constructor(
		private uploadManager: UploadManager,
		private processingManager: ProcessingManager,
		private matDialog: MatDialog,
		private helpService: HelpService,
		private insightsUtility: InsightsUtility,
		public userConfigsService: UserConfigsService,
		private uploadBatchesSdk: UploadBatchesSdk,
		private notificationManager: NotificationManager,
		private uploadBatchesClient: UploadBatchesClient
	) {
		super();

		this.refreshData();
		this.dataSource$ = this.uploadManager.uploadBatchCollection$.pipe(
			filter((collection) => !!collection),
			map((logCollection) => new MatTableDataSource(logCollection?.items))
		);

		this.totalEntries$ = this.uploadManager.uploadBatchCollection$.pipe(
			map((collection) => collection?.totalItems || 0)
		);

		this.hasNextPage$ = this.totalEntries$.pipe(
			observeOn(asyncScheduler),
			map(() => this.paginator?.hasNextPage())
		);

		this.hasPreviousPage$ = this.totalEntries$.pipe(
			observeOn(asyncScheduler),
			map(() => this.paginator?.hasPreviousPage())
		);
		this.helpService.updateHelpLinkUrl('https://sites.google.com/a/ediscoveryplatform.com/help/home/help/case-menu/add-documents');

		// TODO: [live-updates]
		this.refreshInterval = setInterval(() => this.refreshData(), 10000);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		clearInterval(this.refreshInterval);
	}

	public requestDeNistForSelected(): void {
		if (!this.selectedUploadIds.length) {
			return;
		}

		const dialogRef = this.matDialog.open(OkCancelDialogComponent, {
			autoFocus: true,
			data: {
				title: `Perform DeNIST on selected uploads`,
				message: `All processing must be completed before continuing. Are you sure you would like to run the selected task?`
			}
		});

		dialogRef.afterClosed().pipe(
			filter(ok => ok),
			switchMap(() =>
				forkJoin(
					this.selectedUploadIds.map(id =>
						this.uploadBatchesClient.deNISTUploadBatch$(id)
					)
				)
			),
			tap(() => {
				this.selectedUploadIds = [];
				this.notificationManager.displaySnackBarMessage({
					message: 'Starting DeNist for selected upload batches.',
					ttl: 5000
				});
			})
		).subscribe();
	}

	public movePage(event): void {
		this.skip = event.pageIndex * event.pageSize;
		this.take = event.pageSize;
		this.refreshData();
	}

	public loadPreviousPage(): void {
		this.paginator?.previousPage();
	}

	public loadNextPage(): void {
		this.paginator?.nextPage();
	}

	public isSelected(id: string): boolean {
		return this.selectedUploadIds.includes(id);
	}

	public toggleSelected(id: string): void {
		if (this.isSelected(id)) {
			remove(this.selectedUploadIds, (uploadId) => uploadId === id);
		} else {
			this.selectedUploadIds.push(id);
		}
	}

	public deleteSelected(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Upload and Associated Documents`,
			message: `This will PERMANENTLY DELETE the selected upload and documents from the entire case.
          <br/> This action cannot be undone.`
		};
		dialogConfig.panelClass = 'run-task-panel'

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked) => {
					if (okClicked) {
						if (this.selectedUploadIds.length > 1)
							this.notificationManager.displaySnackBarMessage({ message: 'Deleting selected uploads and their documents...', ttl: 5000 });
						else
							this.notificationManager.displaySnackBarMessage({ message: 'Deleting selected upload and its documents...', ttl: 5000 });
						return this.uploadManager.deleteUploads(this.selectedUploadIds);
					}
				})
			)
			.subscribe(() => {
				this.refreshData();
				this.selectedUploadIds = [];
			});
	}

	public selectAll(isChecked: boolean): void {
		if (!isChecked) {
			this.selectedUploadIds = [];
			return;
		}

		this.dataSource$.pipe(take(1)).subscribe((source) => {
			this.selectedUploadIds = source.data.map((item) => item.id);
		});

	}

	public scheduleJob(type: Exclude<JobType, 'deduplicate-docs'>): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Run Task`,
			message: `All processing must be complete before running this step. Are you sure you would like to run the selected task?`
		};

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked) => {
					if (okClicked) {
						if (type === 'de-nist') {
							const request = new DeNistJobRequest();
							request.stepParams = {
								uploadBatchIds: this.selectedUploadIds
							};
							return this.processingManager.scheduleStep({ stepRequest: request });

						} else if (type == 'upload-batch-validation-docs') {
							const request = new UploadBatchValidationJobRequest();
							request.stepParams = {
								uploadBatchIds: this.selectedUploadIds
							}
							return this.processingManager.scheduleStep({ stepRequest: request });
						} else {
							const request = new NoParamJobRequest({ type });
							return this.processingManager.scheduleStep({
								stepRequest: request
							});
						}
					}
				})
			)
			.subscribe(() => {
				this.selectedUploadIds = [];
			});
	}

	public trackByFn(index: number, item: Upload) {
		return `${item.id}`;
	}

	public scheduleDeduplicateJob(byCustodian: boolean = false): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Run Task`,
			message: `All processing must be complete before running this step. Are you sure you would like to run the selected task?`
		};

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked) => {
					if (okClicked) {
						const request = new DeduplicateJobRequest();
						request.stepParams = {
							byCustodian: byCustodian
						};
						return this.processingManager.scheduleStep({ stepRequest: request });

					}
				})
			)
			.subscribe();
	}

	public scheduleJobBatchValidation(uploadBatchIds: string[]): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Run Task`,
			message: `All processing must be complete before running this step. Are you sure you would like to run the selected task?`
		};

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked) => {
					if (okClicked) {
						return this.uploadBatchesSdk.validate(uploadBatchIds);
					}
				})
			)
			.subscribe();
	}

	public startNewUpload(): void {
		const newWindow = window.open('/upload', 'UploadWindow', 'status=no,location=no,menubar=no,directories=no,toolbar=no,width=950,height=600');
		newWindow.focus();
		const timer = setInterval(() => {
			try {
				if (newWindow.closed) {
					this.insightsUtility.event(new UploadWindowClosedEvent());
					clearInterval(timer);
				}
			} catch (e) {
				this.insightsUtility.event(new GeneralError({
					internalError: e,
					message: 'Issue monitoring upload child window.'
				}));
				clearInterval(timer);
			}
		}, 500);
	}

	public shouldShowDatOption(item: Upload) {
		return item.typeId == UploadBatchType.ProductionUpload;
	}

	public sort($event): void {
		this.sortColumn = $event.active;
		this.sortDirection = $event.direction;
		this.refreshData();
	}

	public refreshData(): void {
		this.uploadManager.refreshUploadBatchCollection({
			skip: this.skip,
			take: this.take,
			orderByColumn: this.sortColumn,
			orderByDirection: this.sortDirection
		});
	}
}
